import React, { useMemo, useState } from 'react';
import { Dialog } from '../dialog/Dialog';
import { DialogBody } from '../dialog/DialogBody';
import { DialogFooter } from '../dialog/DialogFooter';
import { CloseButton } from '../button/CloseButton';
import { Button } from '../button/Button';
import { IData } from '../virtual-data/VirtualData';
import { FinalTableColumn } from './FinalTableProps';
import { Space } from '../space/Space';
import { Card } from '../card/Card';
import { Block } from '../block/Block';
import { getColumnField } from './utils';
import { List } from '../list/List';
import { ListItem } from '../list/ListItem';
import { NoData } from '../no-data/NoData';
import { Icon } from '../icon/Icon';
import { mcClear, mcMoveRight, mcMoveDown, mcMoveUp } from '@mc/react-ui-icons';
import { ButtonToolbar } from '../button-toolbar/ButtonToolbar';
import { Title } from '../typography/Title';
import { ModalComponentProps } from '../modal/ModalManager';

type Props<DATA extends IData<ID>, ID extends unknown> = {
  allColumns: FinalTableColumn<DATA>[];
  visibleColumns: string[];
  groupedColumns: string[];
  setVisibleColumns: (visibleColumns: string[]) => void;
} & ModalComponentProps;

export const TableColumnsModal = <DATA extends IData<ID>, ID extends unknown>({
  onClose,
  setVisibleColumns,
  visibleColumns,
  groupedColumns,
  allColumns,
}: Props<DATA, ID>) => {
  const [visible, setVisible] = useState<string[]>(visibleColumns);

  const columns = useMemo(() => {
    return allColumns.filter((column) => column.type !== 'action');
  }, []);

  const columnsMap = useMemo(() => {
    const columnsMap: { [key: string]: FinalTableColumn<DATA> } = {};
    columns.forEach((column) => {
      if (column.type !== 'action') {
        columnsMap[getColumnField(column)] = column;
      }
    });
    return columnsMap;
  }, []);

  const handleAddToVisible = (id: string) => {
    const newVisible = [...visible];
    newVisible.push(id);
    setVisible(newVisible);
  };
  const handleRemoveFromVisible = (id: string) => {
    const newVisible = visible.filter((x) => x !== id);
    setVisible(newVisible);
  };

  const available = columns.filter(
    (x) => visible.indexOf(getColumnField(x)) === -1
  );

  const handleMoveUp = (id: string) => {
    const index = visible.indexOf(id);
    if (index > 0) {
      const newVisible = [...visible];
      newVisible.splice(index, 1);
      newVisible.splice(index - 1, 0, id);
      setVisible(newVisible);
    }
  };

  const handleMoveDown = (id: string) => {
    const index = visible.indexOf(id);
    if (index < visible.length - 1) {
      const newVisible = [...visible];
      newVisible.splice(index, 1);
      newVisible.splice(index + 1, 0, id);
      setVisible(newVisible);
    }
  };

  const handleSubmit = () => {
    onClose();
    setVisibleColumns(visible);
  };

  return (
    <Dialog
      size={'lg'}
      title={'Nastavení sloupců'}
      showOnTop
      style={{ minHeight: '80vh' }}
    >
      <DialogBody disableScroll className={'align-items-stretch'}>
        <Space className={'w-100'} size={'2'}>
          <Card
            mode={'edit'}
            className={'flex-fill w-50'}
            fullHeight
            noScroll
            title={'Dostupné atributy'}
          >
            <Block fullHeight noScroll>
              <List mode={'input'} compact fullHeight>
                {available.map((column) => {
                  return (
                    <ListItem
                      key={getColumnField(column)}
                      // onDoubleClick={() => {
                      //   handleAddToVisible(getColumnField(column));
                      // }}
                      extra={
                        <Button
                          style={'link'}
                          icon={<Icon icon={mcMoveRight} />}
                          onClick={() => {
                            handleAddToVisible(getColumnField(column));
                          }}
                        />
                      }
                    >
                      {columnsMap[getColumnField(column)].header}
                    </ListItem>
                  );
                })}
                {available.length === 0 && <NoData />}
              </List>
            </Block>
          </Card>
          <Card
            mode={'edit'}
            className={'flex-fill w-50'}
            fullHeight
            noScroll
            title={'Zobrazené atributy'}
          >
            <Block fullHeight noScroll>
              <List mode={'input'} compact fullHeight>
                {groupedColumns.length > 0 && (
                  <ListItem disableHover>
                    <Title type={'block'}>Seskupené sloupce</Title>
                  </ListItem>
                )}
                {visible.map((colId, index) => {
                  const isGroupColum = groupedColumns.indexOf(colId) >= 0;
                  const isNextColumnGrouped =
                    index + 1 < visible.length &&
                    groupedColumns.indexOf(visible[index + 1]) >= 0;
                  const isPrevColumnGrouped =
                    index - 1 >= 0 &&
                    groupedColumns.indexOf(visible[index - 1]) >= 0;

                  let listItem = (
                    <ListItem
                      key={colId}
                      // onDoubleClick={() => {
                      //   handleRemoveFromVisible(colId);
                      // }}
                      extra={
                        <ButtonToolbar>
                          <Button
                            style={'link'}
                            disabled={
                              visible.indexOf(colId) === 0 ||
                              (!isGroupColum && isPrevColumnGrouped)
                            }
                            icon={<Icon icon={mcMoveUp} />}
                            onClick={() => {
                              handleMoveUp(colId);
                            }}
                          />
                          <Button
                            style={'link'}
                            disabled={
                              visible.indexOf(colId) === visible.length - 1 ||
                              (isGroupColum && !isNextColumnGrouped)
                            }
                            icon={<Icon icon={mcMoveDown} />}
                            onClick={() => {
                              handleMoveDown(colId);
                            }}
                          />
                          <Button
                            style={'link'}
                            disabled={isGroupColum}
                            title={
                              isGroupColum
                                ? 'Dle sloupce je seskupováno'
                                : undefined
                            }
                            icon={<Icon icon={mcClear} />}
                            onClick={() => {
                              handleRemoveFromVisible(colId);
                            }}
                          />
                        </ButtonToolbar>
                      }
                    >
                      {columnsMap[colId].header}
                    </ListItem>
                  );

                  if (isPrevColumnGrouped && !isGroupColum) {
                    return (
                      <>
                        <ListItem disableHover>
                          <Title type={'block'} className={'mt-4'}>
                            Sloupce
                          </Title>
                        </ListItem>
                        {listItem}
                      </>
                    );
                  } else {
                    return listItem;
                  }
                })}
                {visible.length === 0 && <NoData />}
              </List>
            </Block>
          </Card>
        </Space>
      </DialogBody>
      <DialogFooter>
        <CloseButton onClick={onClose} mode={'edit'} />
        <Button style={'primary'} onClick={handleSubmit} dataTest={'submit'}>
          Uložit
        </Button>
      </DialogFooter>
    </Dialog>
  );
};
